import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LessonCard from './components/LessonCard';
import { geometryLessons1 } from './data/geometryLessons1';
import { geometryLessons2 } from './data/geometryLessons2';
import { geometryLessons3 } from './data/geometryLessons3';
import SEOHead from '../../../components/SEOHead';

export default function Grade10Page() {
  const [activeTab, setActiveTab] = useState<'lessons' | 'worksheets'>('lessons');
  const navigate = useNavigate();
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: 'Grade 10 Geometry | New York Mathematics | Augustine Anyimadu-Ahenkae',
    description: 'Complete Grade 10 Geometry curriculum covering geometric proofs, transformations, similarity, trigonometry, circles, and 3D geometry. Aligned with CCSS, NY Regents, and AP standards.',
    url: `${siteUrl}/grades/grade-10`,
    provider: { '@type': 'Person', name: 'Augustine Anyimadu-Ahenkae', url: siteUrl },
    educationalLevel: 'Grade 10',
    inLanguage: 'en',
    hasCourseInstance: [
      { '@type': 'CourseInstance', name: 'Unit 1: Foundations & Transformations', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Unit 2: Similarity & Right Triangles', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Unit 3: Circles & 3D Geometry', courseMode: 'online' },
    ],
  };

  const allLessons = [...geometryLessons1, ...geometryLessons2, ...geometryLessons3];
  const totalStandards = new Set(allLessons.flatMap(lesson => lesson.standards)).size;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <SEOHead
        title="Grade 10 Geometry New York | Geometric Proofs & Trigonometry | Augustine Anyimadu-Ahenkae"
        description="Master Grade 10 Geometry with interactive lessons on geometric proofs, transformations, similarity, right triangle trigonometry, circles, and 3D solids. Aligned with NY Regents and Common Core standards."
        canonical="/grades/grade-10"
        keywords="Grade 10 Geometry, geometric proofs, NY Regents Geometry, triangle similarity, trigonometry, circle theorems, 10th grade math"
        jsonLd={jsonLd}
      />
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                <i className="ri-graduation-cap-fill text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Mr. Augustine's Math Academy</h1>
                <p className="text-xs text-emerald-600 font-medium">Geometry / Grade 10</p>
              </div>
            </Link>
            <Link
              to="/grades"
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>All Grades
            </Link>
          </div>
        </div>
      </header>

      {/* Navigation Bar */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/grades/hub')}
              className="flex items-center gap-2 text-slate-600 hover:text-indigo-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Back to Grades</span>
            </button>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/grades/grade-9')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Grade 9
              </button>
              <div className="px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600 rounded-lg shadow-md whitespace-nowrap">
                Grade 10
              </div>
              <button
                onClick={() => navigate('/grades/grade-11')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Grade 11
              </button>
              <button
                onClick={() => navigate('/grades/grade-12')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Grade 12
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Teacher Message Banner */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-user-smile-fill text-3xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">Welcome to Geometry — Where Logic Meets Beauty!</h2>
              <p className="text-emerald-50 leading-relaxed max-w-4xl">
                Geometry is one of the most visual and logical branches of mathematics. In this course, you will learn to PROVE
                things — not just calculate them. Every theorem you master here was first proven by mathematicians thousands of
                years ago, and now YOU will prove them too. From the elegance of triangle congruence to the power of trigonometry,
                this course will sharpen your reasoning skills for life. I have seen students who "hated math" fall in love with
                geometry — and I am confident you will too!
              </p>
              <p className="text-white font-semibold mt-3">— Mr. Augustine Anyimadu-Ahenkae</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">{allLessons.length}</p>
              <p className="text-xs text-gray-500 font-medium">Interactive Lessons</p>
            </div>
            <div className="w-px h-8 bg-gray-200"></div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">{allLessons.length * 30}</p>
              <p className="text-xs text-gray-500 font-medium">Practice Questions</p>
            </div>
            <div className="w-px h-8 bg-gray-200"></div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">100+</p>
              <p className="text-xs text-gray-500 font-medium">Printable Worksheets</p>
            </div>
            <div className="w-px h-8 bg-gray-200"></div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">{totalStandards}</p>
              <p className="text-xs text-gray-500 font-medium">Standards Covered</p>
            </div>
            <div className="w-px h-8 bg-gray-200"></div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">4</p>
              <p className="text-xs text-gray-500 font-medium">Standards Frameworks</p>
            </div>
          </div>
        </div>
      </div>

      {/* Geometry State Tests Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-file-list-3-fill text-white text-2xl"></i>
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Geometry State Practice Tests</h3>
                <p className="text-amber-100 text-sm">
                  5 full tests — NY Regents, Ohio, Texas STAAR, Virginia SOL, Pennsylvania Keystone · 150 questions with
                  solutions
                </p>
              </div>
            </div>
            <Link
              to="/grades/geometry-tests"
              className="px-6 py-3 bg-white text-amber-600 rounded-xl font-bold text-sm hover:bg-amber-50 transition-colors shadow-md whitespace-nowrap flex-shrink-0"
            >
              <i className="ri-play-circle-fill mr-2"></i>Take a State Test
            </Link>
          </div>
        </div>
      </div>

      {/* Special Features Banner */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to="/grades/grade-10/constructions"
              className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all border-2 border-white/30 cursor-pointer"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-compass-3-line text-white text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-bold text-base mb-1">Geometric Constructions</h3>
                  <p className="text-teal-100 text-sm">
                    7 interactive compass & straightedge lessons with step-by-step visuals
                  </p>
                </div>
                <i className="ri-arrow-right-line text-white text-xl"></i>
              </div>
            </Link>

            <Link
              to="/grades/grade-10/3d-modeling"
              className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all border-2 border-white/30 cursor-pointer"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-3d-box-line text-white text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-bold text-base mb-1">3D Modeling Activities</h3>
                  <p className="text-teal-100 text-sm">
                    Interactive 3D solids with calculators, nets, and cross-sections
                  </p>
                </div>
                <i className="ri-arrow-right-line text-white text-xl"></i>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Worksheets Preview Banner */}
        <div className="mb-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-8 shadow-xl">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex-1 text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <i className="ri-file-text-line text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold">Practice Worksheets Available</h3>
              </div>
              <p className="text-white/90 text-lg mb-4">
                Master Geometry with our comprehensive worksheet collection — proofs, constructions, transformations, and more
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/worksheets?category=grade-10"
                  className="inline-flex items-center gap-2 bg-white text-purple-600 px-6 py-3 rounded-xl font-semibold hover:bg-purple-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-folder-open-line text-xl"></i>
                  Browse All Grade 10 Worksheets
                </a>
                <button
                  onClick={() => {
                    alert('Free sample worksheet download started! (In production, this would trigger a PDF download)');
                  }}
                  className="inline-flex items-center gap-2 bg-white/10 text-white border-2 border-white/30 px-6 py-3 rounded-xl font-semibold hover:bg-white/20 transition-all backdrop-blur-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-2-line text-xl"></i>
                  Download Free Sample
                </button>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-2xl max-w-sm w-full">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-text-fill text-3xl text-purple-600"></i>
                </div>
                <div className="flex-1">
                  <div className="inline-block bg-purple-100 text-purple-700 text-xs font-bold px-3 py-1 rounded-full mb-2">
                    FREE SAMPLE
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Triangle Congruence Proofs</h4>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-file-line"></i>
                      7 pages
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calculator-line"></i>
                      20 problems
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      ))}
                      {[4, 5].map((i) => (
                        <div key={i} className="w-2 h-2 bg-gray-300 rounded-full"></div>
                      ))}
                    </div>
                    <span className="text-xs text-gray-600 font-medium">Intermediate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 bg-white rounded-2xl p-2 shadow-lg">
          <button
            onClick={() => setActiveTab('lessons')}
            className={`flex-1 px-6 py-4 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'lessons'
                ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg'
                : 'text-gray-600 hover:text-indigo-600'
            }`}
          >
            <i className="ri-book-open-line mr-2"></i>
            Video Lessons
          </button>
          <button
            onClick={() => setActiveTab('worksheets')}
            className={`flex-1 px-6 py-4 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'worksheets'
                ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg'
                : 'text-gray-600 hover:text-indigo-600'
            }`}
          >
            <i className="ri-file-text-line mr-2"></i>
            Practice Worksheets
          </button>
        </div>

        {/* Content */}
        {activeTab === 'lessons' ? (
          <>
            {/* Unit 1: Foundations & Transformations */}
            <section className="mb-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-compass-3-line text-white text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Unit 1: Foundations & Transformations</h2>
                  <p className="text-gray-600 text-sm">Geometric reasoning, proofs, and rigid motions</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {geometryLessons1.map((lesson) => (
                  <LessonCard key={lesson.id} lesson={lesson} />
                ))}
              </div>
            </section>

            {/* Unit 2: Similarity & Right Triangles */}
            <section className="mb-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-triangle-line text-white text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Unit 2: Similarity & Right Triangles</h2>
                  <p className="text-gray-600 text-sm">Proportional reasoning, trigonometry, and quadrilaterals</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {geometryLessons2.map((lesson) => (
                  <LessonCard key={lesson.id} lesson={lesson} />
                ))}
              </div>
            </section>

            {/* Unit 3: Circles & 3D Geometry */}
            <section>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-circle-line text-white text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Unit 3: Circles & 3D Geometry</h2>
                  <p className="text-gray-600 text-sm">Circle theorems, coordinate geometry, and volume</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {geometryLessons3.map((lesson) => (
                  <LessonCard key={lesson.id} lesson={lesson} />
                ))}
              </div>
            </section>
          </>
        ) : (
          <div className="space-y-6">
            {/* Worksheets Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Free Sample Worksheet */}
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-purple-500">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl flex items-center justify-center">
                    <i className="ri-file-text-fill text-2xl text-purple-600"></i>
                  </div>
                  <span className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">FREE</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Triangle Congruence Proofs</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Practice SSS, SAS, ASA, AAS, and HL congruence theorems with step-by-step proof writing
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <span className="flex items-center gap-1">
                    <i className="ri-file-line"></i>
                    7 pages
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calculator-line"></i>
                    20 problems
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex gap-1">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    ))}
                    {[4, 5].map((i) => (
                      <div key={i} className="w-2 h-2 bg-gray-300 rounded-full"></div>
                    ))}
                  </div>
                  <span className="text-xs text-gray-600 font-medium">Intermediate</span>
                </div>
                <button
                  onClick={() => {
                    alert('Free sample worksheet download started! (In production, this would trigger a PDF download)');
                  }}
                  className="w-full bg-purple-500 text-white py-3 rounded-xl font-semibold hover:bg-purple-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-2-line mr-2"></i>
                  Download Free Sample
                </button>
              </div>

              {/* Locked Worksheets Preview */}
              {[
                {
                  title: 'Circle Theorems & Arcs',
                  description: 'Master central angles, inscribed angles, and arc measures with proofs',
                  pages: 9,
                  problems: 28,
                  difficulty: 4,
                },
                {
                  title: 'Coordinate Geometry Proofs',
                  description: 'Prove geometric properties using distance, midpoint, and slope formulas',
                  pages: 8,
                  problems: 24,
                  difficulty: 5,
                },
                {
                  title: 'Transformations & Symmetry',
                  description: 'Explore reflections, rotations, translations, and dilations on the coordinate plane',
                  pages: 10,
                  problems: 32,
                  difficulty: 3,
                },
                {
                  title: 'Right Triangle Trigonometry',
                  description: 'Apply sine, cosine, and tangent ratios to solve real-world problems',
                  pages: 7,
                  problems: 26,
                  difficulty: 4,
                },
                {
                  title: 'Similarity & Proportions',
                  description: 'Practice AA, SAS~, and SSS~ similarity with proportional reasoning',
                  pages: 8,
                  problems: 30,
                  difficulty: 3,
                },
              ].map((worksheet, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-gray-200 relative overflow-hidden cursor-pointer"
                  onClick={() => {
                    const membershipSection = document.getElementById('membership-upsell');
                    if (membershipSection) {
                      membershipSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                >
                  <div className="absolute top-4 right-4 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <i className="ri-lock-fill text-amber-600"></i>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-file-text-line text-2xl text-gray-400"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{worksheet.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{worksheet.description}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    <span className="flex items-center gap-1">
                      <i className="ri-file-line"></i>
                      {worksheet.pages} pages
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calculator-line"></i>
                      {worksheet.problems} problems
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className={`w-2 h-2 rounded-full ${
                            i <= worksheet.difficulty ? 'bg-purple-500' : 'bg-gray-300'
                          }`}
                        ></div>
                      ))}
                    </div>
                    <span className="text-xs text-gray-600 font-medium">
                      {worksheet.difficulty <= 2
                        ? 'Beginner'
                        : worksheet.difficulty <= 3
                        ? 'Intermediate'
                        : 'Advanced'}
                    </span>
                  </div>
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3 text-center">
                    <p className="text-sm text-amber-800 font-semibold">
                      <i className="ri-vip-crown-line mr-1"></i>
                      Members Only
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Membership Upsell */}
            <div
              id="membership-upsell"
              className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-2xl p-8 text-center shadow-2xl"
            >
              <div className="max-w-2xl mx-auto">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                  <i className="ri-vip-crown-fill text-3xl text-white"></i>
                </div>
                <h3 className="text-3xl font-bold text-white mb-3">Unlock All Grade 10 Worksheets</h3>
                <p className="text-white/90 text-lg mb-6">
                  Get instant access to 100+ practice worksheets covering every Geometry topic — with full solutions and
                  step-by-step explanations
                </p>
                <div className="flex flex-wrap justify-center gap-4 mb-6">
                  <div className="flex items-center gap-2 text-white">
                    <i className="ri-check-line text-xl"></i>
                    <span>Unlimited Downloads</span>
                  </div>
                  <div className="flex items-center gap-2 text-white">
                    <i className="ri-check-line text-xl"></i>
                    <span>Detailed Solutions</span>
                  </div>
                  <div className="flex items-center gap-2 text-white">
                    <i className="ri-check-line text-xl"></i>
                    <span>New Worksheets Weekly</span>
                  </div>
                </div>
                <a
                  href="/worksheets?category=grade-10"
                  className="inline-flex items-center gap-2 bg-white text-purple-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-purple-50 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
                >
                  View All Grade 10 Worksheets
                  <i className="ri-arrow-right-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-graduation-cap-fill text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white">Mr. Augustine's Math Academy</h3>
            </div>
            <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
              Empowering students to master mathematics through proven teaching methods and personalized support.
            </p>
            <div className="flex justify-center gap-4 mb-6">
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors">
                <i className="ri-facebook-fill"></i>
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors">
                <i className="ri-twitter-fill"></i>
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors">
                <i className="ri-youtube-fill"></i>
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors">
                <i className="ri-mail-fill"></i>
              </a>
            </div>
            <p className="text-sm text-gray-500">© 2024 Mr. Augustine's Math Academy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}